import { useEffect, useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Barcode,
  Boxes,
  Check,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  EmptyState,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Stat,
  StatRow,
  TableSkeleton,
} from '@/ui/Panel';
import { ChipGroup, Pagination, SubNav, SubNavItem } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Checkbox, Field, Input, MoneyInput, SearchInput, Select } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { useDebouncedValue } from '@/ui/MultiSelect';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import {
  type AttachBarcodeResult,
  attachSuccessToast,
} from '@/features/catalog/barcodeReview';
import {
  BARCODE_TYPES,
  useUpdateBarcode,
  type BarcodeDraft,
} from '@/features/catalog/barcodeMutations';
import { humanize, money, num, relativeTime, smartDate } from '@/lib/format';
import type {
  BarcodeLookupRow,
  BarcodeReviewIssue,
  BrandSummary,
  DepartmentListResponse,
  DepartmentSummary,
  PaginatedResponse,
  ProductListResponse,
  ProductSummary,
  StoreZoneSummary,
} from '@/lib/api/types';
import { asText } from '@/lib/searchParams';

/* Organize — the catalog's reference data.

   Departments, brands, zones, barcodes and the label queue are all small,
   rarely-changed lists that the old admin scattered across five screens. They
   share one screen and one shape here: a table you can edit in place. The tab
   lives in the URL so a link to "the scan-miss queue" is a real link. */

const TABS = ['departments', 'brands', 'zones', 'barcodes', 'labels'] as const;
type Tab = (typeof TABS)[number];

type IssueStatus = 'open' | 'resolved' | 'ignored';
type IssueType = 'all' | 'miss' | 'fallback';
type LabelStatus = 'all' | 'pending' | 'claimed' | 'printed' | 'discarded';

interface OrganizeSearch {
  tab: Tab;
  q: string;
  issueStatus: IssueStatus;
  issueType: IssueType;
  labelStatus: LabelStatus;
  page: number;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

export const Route = createFileRoute('/_app/organize')({
  staticData: { crumb: 'Organize' },
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput): OrganizeSearch => ({
    tab: oneOf(s.tab, TABS, 'departments'),
    q: asText(s.q, ''),
    issueStatus: oneOf(s.issueStatus, ['open', 'resolved', 'ignored'] as const, 'open'),
    issueType: oneOf(s.issueType, ['all', 'miss', 'fallback'] as const, 'all'),
    labelStatus: oneOf(
      s.labelStatus,
      ['all', 'pending', 'claimed', 'printed', 'discarded'] as const,
      'pending'
    ),
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: OrganizeScreen,
});

const TAB_LABELS: Record<Tab, string> = {
  departments: 'Departments',
  brands: 'Brands',
  zones: 'Zones',
  barcodes: 'Barcodes',
  labels: 'Labels',
};

function OrganizeScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  function patch(next: Partial<OrganizeSearch>) {
    void navigate({ search: (prev: OrganizeSearch) => ({ ...prev, ...next }) });
  }

  const labelSummary = useQuery({
    queryKey: ['labels', 'summary'],
    queryFn: () => api.get<LabelQueueSummary>('/labels/queue/summary'),
    staleTime: 30_000,
  });

  const openIssues = useQuery({
    queryKey: ['barcodes', 'issues', 'count'],
    queryFn: () =>
      api.get<PaginatedResponse<BarcodeReviewIssue>>('/barcodes/review/issues', {
        params: { status: 'open', type: 'all', pageSize: 1 },
      }),
    staleTime: 60_000,
  });

  return (
    <Page>
      <PageHeader
        eyebrow="Catalog"
        title="Organize"
        subtitle="Departments, brands, zones, barcodes and the shelf-label queue."
        tabs={
          <SubNav>
            {TABS.map((tab) => (
              <SubNavItem
                key={tab}
                active={search.tab === tab}
                onClick={() => patch({ tab, page: 1, q: '' })}
                count={
                  tab === 'labels'
                    ? labelSummary.data?.pending
                    : tab === 'barcodes'
                      ? openIssues.data?.total
                      : undefined
                }
              >
                {TAB_LABELS[tab]}
              </SubNavItem>
            ))}
          </SubNav>
        }
      />
      <PageBody className="space-y-3">
        {search.tab === 'departments' ? <DepartmentsTab /> : null}
        {search.tab === 'brands' ? <BrandsTab /> : null}
        {search.tab === 'zones' ? <ZonesTab /> : null}
        {search.tab === 'barcodes' ? <BarcodesTab search={search} patch={patch} /> : null}
        {search.tab === 'labels' ? <LabelsTab search={search} patch={patch} /> : null}
      </PageBody>
    </Page>
  );
}

/* ── Shared bits ──────────────────────────────────────────────────────────── */

function HeadRow({ children, cols }: { children: ReactNode; cols: string }) {
  return (
    <div
      className="border-line bg-surface-sunken text-2xs text-ink-faint grid items-center gap-x-3 border-b px-3 py-1.5 font-medium tracking-wider uppercase"
      style={{ gridTemplateColumns: cols }}
    >
      {children}
    </div>
  );
}

function BodyRow({
  children,
  cols,
  editing = false,
}: {
  children: ReactNode;
  cols: string;
  editing?: boolean;
}) {
  return (
    <div
      className={`border-line-soft grid items-center gap-x-3 border-b px-3 text-sm ${
        editing ? 'bg-signal-muted py-1.5' : 'hover:bg-surface-hover min-h-7 py-1'
      }`}
      style={{ gridTemplateColumns: cols }}
    >
      {children}
    </div>
  );
}

function RowActions({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-0.5">{children}</div>;
}

/* ── Departments ──────────────────────────────────────────────────────────── */

interface DeptDraft {
  code: string;
  name: string;
  taxRate: string;
  ebtDefault: boolean;
  sortOrder: number;
  active: boolean;
}

const DEPT_COLS = '7rem minmax(0,1fr) 6rem 4rem 5rem 4rem 5rem';

function DepartmentsTab() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DeptDraft | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DepartmentSummary | null>(null);

  const list = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
  });

  function done() {
    setEditingId(null);
    setCreating(false);
    setDraft(null);
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
  }

  const save = useMutation({
    mutationFn: (vars: { id: number | null; body: DeptDraft }) => {
      // tax_rate is a Postgres numeric — an empty string is not "no rate", it
      // is a cast error. Omit the key instead.
      const { taxRate, ...rest } = vars.body;
      const body = {
        ...rest,
        code: rest.code.trim(),
        name: rest.name.trim(),
        ...(taxRate.trim() ? { taxRate: taxRate.trim() } : {}),
      };
      return vars.id === null
        ? api.post('/departments', body)
        : api.put(`/departments/${vars.id}`, body);
    },
    onSuccess: (_r, vars) => {
      toast.success(vars.id === null ? 'Department created' : 'Department saved');
      done();
    },
    onError: (err) =>
      toast.error('Could not save department', { description: errorMessage(err) }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/departments/${id}`),
    onSuccess: () => {
      toast.success('Department deleted');
      setPendingDelete(null);
      done();
    },
    onError: (err) =>
      toast.error('Could not delete department', { description: errorMessage(err) }),
  });

  function startEdit(d: DepartmentSummary) {
    setCreating(false);
    setEditingId(d.id);
    setDraft({
      code: d.code,
      name: d.name,
      taxRate: d.taxRate ?? '',
      ebtDefault: d.ebtDefault !== false,
      sortOrder: d.sortOrder ?? 0,
      active: d.active !== false,
    });
  }

  function startCreate() {
    setEditingId(null);
    setCreating(true);
    setDraft({
      code: '',
      name: '',
      taxRate: '',
      ebtDefault: true,
      sortOrder: 0,
      active: true,
    });
  }

  const rows = list.data?.data ?? [];

  return (
    <Panel
      bleed
      title="Departments"
      description="Tax rate and EBT default flow from here to every product in the department."
      actions={
        <Button size="xs" icon={<Plus className="size-3" />} onClick={startCreate}>
          New department
        </Button>
      }
    >
      <HeadRow cols={DEPT_COLS}>
        <div>Code</div>
        <div>Name</div>
        <div className="text-right">Tax rate</div>
        <div className="text-center">EBT</div>
        <div className="text-right">Sort</div>
        <div className="text-center">Active</div>
        <div />
      </HeadRow>

      {creating && draft ? (
        <DeptEditRow
          draft={draft}
          onChange={setDraft}
          saving={save.isPending}
          onCancel={() => {
            setCreating(false);
            setDraft(null);
          }}
          onSave={() => save.mutate({ id: null, body: draft })}
        />
      ) : null}

      {list.isLoading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : list.error ? (
        <ErrorState
          className="m-3"
          message={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      ) : rows.length === 0 && !creating ? (
        <EmptyState compact icon={<Boxes />} title="No departments yet" />
      ) : (
        rows.map((d) =>
          editingId === d.id && draft ? (
            <DeptEditRow
              key={d.id}
              draft={draft}
              onChange={setDraft}
              saving={save.isPending}
              onCancel={() => {
                setEditingId(null);
                setDraft(null);
              }}
              onSave={() => save.mutate({ id: d.id, body: draft })}
            />
          ) : (
            <BodyRow key={d.id} cols={DEPT_COLS}>
              <Code>{d.code}</Code>
              <span className="text-ink truncate text-xs">{d.name}</span>
              <span className="text-ink-muted text-right font-mono text-xs tabular-nums">
                {d.taxRate ?? '—'}
              </span>
              <span className="text-center">
                {d.ebtDefault !== false ? (
                  <Check className="text-positive mx-auto size-3.5" />
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </span>
              <span className="text-ink-muted text-right font-mono text-xs tabular-nums">
                {num(d.sortOrder ?? 0)}
              </span>
              <span className="text-center">
                {d.active !== false ? (
                  <Badge tone="positive">On</Badge>
                ) : (
                  <Badge tone="warn">Off</Badge>
                )}
              </span>
              <RowActions>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="Edit"
                  onClick={() => startEdit(d)}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="Delete"
                  onClick={() => setPendingDelete(d)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </RowActions>
            </BodyRow>
          )
        )
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this department?"
        description={
          pendingDelete ? `${pendingDelete.code} — ${pendingDelete.name}` : undefined
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        consequences={[
          'This is a permanent delete, not a deactivation.',
          'It will be refused while any product or sub-department still points at it.',
          'Any promotion on this department ends; registers drop it on their next sync.',
          'To retire a department in use, switch it to inactive instead.',
        ]}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </Panel>
  );
}

function DeptEditRow({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  draft: DeptDraft;
  onChange: (next: DeptDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const valid = draft.code.trim() !== '' && draft.name.trim() !== '';
  return (
    <BodyRow cols={DEPT_COLS} editing>
      <Input
        size="sm"
        mono
        autoFocus
        value={draft.code}
        placeholder="CODE"
        onChange={(e) => onChange({ ...draft, code: e.currentTarget.value })}
      />
      <Input
        size="sm"
        value={draft.name}
        placeholder="Name"
        onChange={(e) => onChange({ ...draft, name: e.currentTarget.value })}
      />
      <Input
        size="sm"
        mono
        inputMode="decimal"
        value={draft.taxRate}
        placeholder="0.000"
        onChange={(e) => onChange({ ...draft, taxRate: e.currentTarget.value })}
      />
      <div className="flex justify-center">
        <Checkbox
          checked={draft.ebtDefault}
          onCheckedChange={(v) => onChange({ ...draft, ebtDefault: v })}
        />
      </div>
      <Input
        size="sm"
        mono
        inputMode="numeric"
        value={draft.sortOrder}
        onChange={(e) =>
          onChange({ ...draft, sortOrder: Number(e.currentTarget.value) || 0 })
        }
      />
      <div className="flex justify-center">
        <Checkbox
          checked={draft.active}
          onCheckedChange={(v) => onChange({ ...draft, active: v })}
        />
      </div>
      <RowActions>
        <Button
          size="icon-sm"
          variant="primary"
          title="Save"
          disabled={!valid}
          loading={saving}
          onClick={onSave}
        >
          <Check className="size-3.5" />
        </Button>
        <Button size="icon-sm" variant="ghost" title="Cancel" onClick={onCancel}>
          <X className="size-3.5" />
        </Button>
      </RowActions>
    </BodyRow>
  );
}

/* ── Brands ───────────────────────────────────────────────────────────────── */

const BRAND_COLS = 'minmax(0,1fr) 7rem 5rem';

function BrandsTab() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<BrandSummary | null>(null);

  const list = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
  });

  function done() {
    setEditingId(null);
    setCreating(false);
    setName('');
    void queryClient.invalidateQueries({ queryKey: ['brands'] });
  }

  const save = useMutation({
    mutationFn: (vars: { id: number | null; name: string }) =>
      vars.id === null
        ? api.post('/brands', { name: vars.name })
        : api.put(`/brands/${vars.id}`, { name: vars.name }),
    onSuccess: (_r, vars) => {
      toast.success(vars.id === null ? 'Brand created' : 'Brand renamed');
      done();
    },
    onError: (err) =>
      toast.error('Could not save brand', { description: errorMessage(err) }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/brands/${id}`),
    onSuccess: () => {
      toast.success('Brand deleted');
      setPendingDelete(null);
      done();
    },
    onError: (err) =>
      toast.error('Could not delete brand', { description: errorMessage(err) }),
  });

  const rows = list.data ?? [];

  return (
    <Panel
      bleed
      title="Brands"
      description="A brand is only a label on a product — the count tells you what a rename touches."
      actions={
        <Button
          size="xs"
          icon={<Plus className="size-3" />}
          onClick={() => {
            setEditingId(null);
            setName('');
            setCreating(true);
          }}
        >
          New brand
        </Button>
      }
    >
      <HeadRow cols={BRAND_COLS}>
        <div>Name</div>
        <div className="text-right">Products</div>
        <div />
      </HeadRow>

      {creating ? (
        <BodyRow cols={BRAND_COLS} editing>
          <Input
            size="sm"
            autoFocus
            value={name}
            placeholder="Brand name"
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim())
                save.mutate({ id: null, name: name.trim() });
            }}
          />
          <span />
          <RowActions>
            <Button
              size="icon-sm"
              variant="primary"
              title="Save"
              disabled={!name.trim()}
              loading={save.isPending}
              onClick={() => save.mutate({ id: null, name: name.trim() })}
            >
              <Check className="size-3.5" />
            </Button>
            <Button
              size="icon-sm"
              variant="ghost"
              title="Cancel"
              onClick={() => setCreating(false)}
            >
              <X className="size-3.5" />
            </Button>
          </RowActions>
        </BodyRow>
      ) : null}

      {list.isLoading ? (
        <TableSkeleton rows={6} cols={3} />
      ) : list.error ? (
        <ErrorState
          className="m-3"
          message={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      ) : rows.length === 0 && !creating ? (
        <EmptyState compact icon={<Tag />} title="No brands yet" />
      ) : (
        rows.map((b) =>
          editingId === b.id ? (
            <BodyRow key={b.id} cols={BRAND_COLS} editing>
              <Input
                size="sm"
                autoFocus
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim())
                    save.mutate({ id: b.id, name: name.trim() });
                }}
              />
              <span className="text-ink-muted text-right font-mono text-xs tabular-nums">
                {num(b.productCount ?? 0)}
              </span>
              <RowActions>
                <Button
                  size="icon-sm"
                  variant="primary"
                  title="Save"
                  disabled={!name.trim()}
                  loading={save.isPending}
                  onClick={() => save.mutate({ id: b.id, name: name.trim() })}
                >
                  <Check className="size-3.5" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="Cancel"
                  onClick={() => setEditingId(null)}
                >
                  <X className="size-3.5" />
                </Button>
              </RowActions>
            </BodyRow>
          ) : (
            <BodyRow key={b.id} cols={BRAND_COLS}>
              <span className="text-ink truncate text-xs">{b.name}</span>
              <span className="text-ink-muted text-right font-mono text-xs tabular-nums">
                {num(b.productCount ?? 0)}
              </span>
              <RowActions>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="Rename"
                  onClick={() => {
                    setCreating(false);
                    setEditingId(b.id);
                    setName(b.name);
                  }}
                >
                  <Pencil className="size-3.5" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="Delete"
                  onClick={() => setPendingDelete(b)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </RowActions>
            </BodyRow>
          )
        )
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this brand?"
        description={pendingDelete?.name}
        confirmLabel="Delete"
        loading={remove.isPending}
        consequences={[
          'This is a permanent delete, not a deactivation.',
          pendingDelete?.productCount
            ? `${num(pendingDelete.productCount)} product(s) still reference this brand — the server will refuse.`
            : 'No product references this brand.',
        ]}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </Panel>
  );
}

/* ── Zones ────────────────────────────────────────────────────────────────── */

interface ZoneDraft {
  name: string;
  zoneType: string;
  sortOrder: number;
  description: string;
}

const ZONE_COLS = 'minmax(0,12rem) 7rem 4rem minmax(0,1fr) 4rem 5rem';
const ZONE_TYPES = ['aisle', 'cooler', 'freezer', 'backroom', 'display', 'other'];

function ZonesTab() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ZoneDraft | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<StoreZoneSummary | null>(null);

  const list = useQuery({
    queryKey: ['zones'],
    queryFn: () => api.get<StoreZoneSummary[]>('/zones'),
  });

  function done() {
    setEditingId(null);
    setCreating(false);
    setDraft(null);
    void queryClient.invalidateQueries({ queryKey: ['zones'] });
  }

  const save = useMutation({
    mutationFn: (vars: { id: number | null; body: ZoneDraft }) => {
      const body = {
        name: vars.body.name.trim(),
        zoneType: vars.body.zoneType,
        sortOrder: vars.body.sortOrder,
        description: vars.body.description.trim() || null,
      };
      return vars.id === null
        ? api.post('/zones', body)
        : api.put(`/zones/${vars.id}`, body);
    },
    onSuccess: (_r, vars) => {
      toast.success(vars.id === null ? 'Zone created' : 'Zone saved');
      done();
    },
    onError: (err) =>
      toast.error('Could not save zone', { description: errorMessage(err) }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/zones/${id}`),
    onSuccess: () => {
      toast.success('Zone retired');
      setPendingDelete(null);
      done();
    },
    onError: (err) =>
      toast.error('Could not retire zone', { description: errorMessage(err) }),
  });

  const restore = useMutation({
    mutationFn: (zone: StoreZoneSummary) =>
      api.put(`/zones/${zone.id}`, { active: true }),
    onSuccess: () => {
      toast.success('Zone restored');
      done();
    },
    onError: (err) =>
      toast.error('Could not restore zone', { description: errorMessage(err) }),
  });

  const rows = list.data ?? [];

  return (
    <Panel
      bleed
      title="Zones"
      description="Zones plus a product's pick order define the walking route a picker follows."
      actions={
        <Button
          size="xs"
          icon={<Plus className="size-3" />}
          onClick={() => {
            setEditingId(null);
            setCreating(true);
            setDraft({ name: '', zoneType: 'aisle', sortOrder: 0, description: '' });
          }}
        >
          New zone
        </Button>
      }
    >
      <HeadRow cols={ZONE_COLS}>
        <div>Name</div>
        <div>Type</div>
        <div className="text-right">Sort</div>
        <div>Description</div>
        <div className="text-center">Active</div>
        <div />
      </HeadRow>

      {creating && draft ? (
        <ZoneEditRow
          draft={draft}
          onChange={setDraft}
          saving={save.isPending}
          onCancel={() => {
            setCreating(false);
            setDraft(null);
          }}
          onSave={() => save.mutate({ id: null, body: draft })}
        />
      ) : null}

      {list.isLoading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : list.error ? (
        <ErrorState
          className="m-3"
          message={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      ) : rows.length === 0 && !creating ? (
        <EmptyState compact icon={<Boxes />} title="No zones yet" />
      ) : (
        rows.map((z) =>
          editingId === z.id && draft ? (
            <ZoneEditRow
              key={z.id}
              draft={draft}
              onChange={setDraft}
              saving={save.isPending}
              onCancel={() => {
                setEditingId(null);
                setDraft(null);
              }}
              onSave={() => save.mutate({ id: z.id, body: draft })}
            />
          ) : (
            <BodyRow key={z.id} cols={ZONE_COLS}>
              <span className="text-ink truncate text-xs">{z.name}</span>
              <span className="text-ink-muted text-xs">
                {humanize(z.zoneType ?? 'aisle')}
              </span>
              <span className="text-ink-muted text-right font-mono text-xs tabular-nums">
                {num(z.sortOrder ?? 0)}
              </span>
              <span className="text-2xs text-ink-faint truncate">
                {z.description ?? '—'}
              </span>
              <span className="text-center">
                {z.active !== false ? (
                  <Badge tone="positive">On</Badge>
                ) : (
                  <Badge tone="warn">Retired</Badge>
                )}
              </span>
              <RowActions>
                {z.active !== false ? (
                  <>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="Edit"
                      onClick={() => {
                        setCreating(false);
                        setEditingId(z.id);
                        setDraft({
                          name: z.name,
                          zoneType: z.zoneType ?? 'aisle',
                          sortOrder: z.sortOrder ?? 0,
                          description: z.description ?? '',
                        });
                      }}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="Retire"
                      onClick={() => setPendingDelete(z)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="xs"
                    variant="ghost"
                    loading={restore.isPending}
                    onClick={() => restore.mutate(z)}
                  >
                    Restore
                  </Button>
                )}
              </RowActions>
            </BodyRow>
          )
        )
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Retire this zone?"
        description={pendingDelete?.name}
        confirmLabel="Retire"
        loading={remove.isPending}
        consequences={[
          'The zone is deactivated, not erased — products keep pointing at it.',
          'It disappears from the zone picker on new product edits.',
          'You can restore it from this list afterwards.',
        ]}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </Panel>
  );
}

function ZoneEditRow({
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  draft: ZoneDraft;
  onChange: (next: ZoneDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <BodyRow cols={ZONE_COLS} editing>
      <Input
        size="sm"
        autoFocus
        value={draft.name}
        placeholder="Zone name"
        onChange={(e) => onChange({ ...draft, name: e.currentTarget.value })}
      />
      <Select
        size="sm"
        value={draft.zoneType}
        onChange={(e) => onChange({ ...draft, zoneType: e.currentTarget.value })}
      >
        {ZONE_TYPES.map((t) => (
          <option key={t} value={t}>
            {humanize(t)}
          </option>
        ))}
      </Select>
      <Input
        size="sm"
        mono
        inputMode="numeric"
        value={draft.sortOrder}
        onChange={(e) =>
          onChange({ ...draft, sortOrder: Number(e.currentTarget.value) || 0 })
        }
      />
      <Input
        size="sm"
        value={draft.description}
        placeholder="Optional"
        onChange={(e) => onChange({ ...draft, description: e.currentTarget.value })}
      />
      <span />
      <RowActions>
        <Button
          size="icon-sm"
          variant="primary"
          title="Save"
          disabled={!draft.name.trim()}
          loading={saving}
          onClick={onSave}
        >
          <Check className="size-3.5" />
        </Button>
        <Button size="icon-sm" variant="ghost" title="Cancel" onClick={onCancel}>
          <X className="size-3.5" />
        </Button>
      </RowActions>
    </BodyRow>
  );
}

/* ── Barcodes: scan-miss review queue + the code catalog ───────────────────── */

const ISSUE_COLS = 'minmax(0,11rem) 5rem 4rem minmax(0,1fr) 7rem 13rem';
const CODE_COLS = 'minmax(0,11rem) minmax(0,1fr) 6rem 5rem 4rem';

function BarcodesTab({
  search,
  patch,
}: {
  search: OrganizeSearch;
  patch: (next: Partial<OrganizeSearch>) => void;
}) {
  const queryClient = useQueryClient();
  const [attachTo, setAttachTo] = useState<BarcodeReviewIssue | null>(null);
  const [createFrom, setCreateFrom] = useState<BarcodeReviewIssue | null>(null);
  const [rawQuery, setRawQuery] = useState(search.q);
  const debounced = useDebouncedValue(rawQuery);

  // Inline edit of a catalog code. One row at a time — this is a reference
  // table, not a spreadsheet, and a half-finished edit two screens up is how
  // the wrong barcode gets saved.
  const [editingCode, setEditingCode] = useState<number | null>(null);
  const [codeDraft, setCodeDraft] = useState<BarcodeDraft | null>(null);

  function cancelCodeEdit() {
    setEditingCode(null);
    setCodeDraft(null);
  }

  function startCodeEdit(row: BarcodeLookupRow) {
    setEditingCode(row.id);
    setCodeDraft({ code: row.code, type: row.type, isPrimary: row.isPrimary === true });
  }

  const saveCode = useUpdateBarcode(cancelCodeEdit);

  useEffect(() => {
    if (debounced !== search.q) patch({ q: debounced, page: 1 });
    // Keyed on the debounced value alone — `patch` and `search.q` change as a
    // result of this effect, so listing them would re-enter it on its own
    // navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const issues = useQuery({
    queryKey: ['barcodes', 'issues', search.issueStatus, search.issueType],
    queryFn: () =>
      api.get<PaginatedResponse<BarcodeReviewIssue>>('/barcodes/review/issues', {
        params: { status: search.issueStatus, type: search.issueType, pageSize: 100 },
      }),
  });

  const codes = useQuery({
    queryKey: ['barcodes', 'catalog', search.q, search.page],
    queryFn: () =>
      api.get<PaginatedResponse<BarcodeLookupRow>>('/barcodes', {
        params: { search: search.q || undefined, page: search.page, pageSize: 50 },
      }),
    placeholderData: (prev) => prev,
  });

  function refreshIssues() {
    void queryClient.invalidateQueries({ queryKey: ['barcodes'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  }

  const resolve = useMutation({
    mutationFn: (vars: { id: number; action: 'resolve' | 'ignore' }) =>
      api.post(`/barcodes/review/issues/${vars.id}/${vars.action}`, {}),
    onSuccess: (_r, vars) => {
      toast.success(vars.action === 'resolve' ? 'Marked resolved' : 'Ignored');
      refreshIssues();
    },
    onError: (err) =>
      toast.error('Could not update issue', { description: errorMessage(err) }),
  });

  const issueRows = issues.data?.data ?? [];

  return (
    <>
      <Panel
        bleed
        title="Scan-miss review"
        description="Codes a register scanned but could not resolve. Each row is a sale that had to be rung the hard way."
        actions={
          <div className="flex items-center gap-1.5">
            <ChipGroup<IssueStatus>
              value={search.issueStatus}
              onChange={(v) => patch({ issueStatus: v })}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'ignored', label: 'Ignored' },
              ]}
            />
            <ChipGroup<IssueType>
              value={search.issueType}
              onChange={(v) => patch({ issueType: v })}
              options={[
                { value: 'all', label: 'All' },
                { value: 'miss', label: 'Miss' },
                { value: 'fallback', label: 'Fallback' },
              ]}
            />
          </div>
        }
      >
        <HeadRow cols={ISSUE_COLS}>
          <div>Scanned code</div>
          <div>Kind</div>
          <div className="text-right">Hits</div>
          <div>Matched / note</div>
          <div>Last seen</div>
          <div />
        </HeadRow>

        {issues.isLoading ? (
          <TableSkeleton rows={5} cols={5} />
        ) : issues.error ? (
          <ErrorState
            className="m-3"
            message={errorMessage(issues.error)}
            onRetry={() => void issues.refetch()}
          />
        ) : issueRows.length === 0 ? (
          <EmptyState
            compact
            icon={<Barcode />}
            title={search.issueStatus === 'open' ? 'Nothing to review' : 'Nothing here'}
            description={
              search.issueStatus === 'open'
                ? 'Every scanned code resolved to a product.'
                : undefined
            }
          />
        ) : (
          issueRows.map((issue) => (
            <BodyRow key={issue.id} cols={ISSUE_COLS}>
              <Code copyable>{issue.rawBarcode}</Code>
              <span>
                {issue.issueType === 'miss' ? (
                  <Badge tone="negative">Miss</Badge>
                ) : (
                  <Badge tone="warn">Fallback</Badge>
                )}
              </span>
              <span className="text-ink text-right font-mono text-xs tabular-nums">
                {num(issue.totalCount)}
              </span>
              <span className="text-2xs text-ink-muted truncate">
                {issue.productName ??
                  issue.resolutionNote ??
                  (issue.fallbackBarcode ? `Fell back to ${issue.fallbackBarcode}` : '—')}
              </span>
              <span
                className="text-2xs text-ink-faint truncate"
                title={`${issue.lastRegisterName ?? ''} · ${smartDate(issue.lastSeenAt)}`}
              >
                {relativeTime(issue.lastSeenAt)}
              </span>
              <RowActions>
                {issue.status === 'open' ? (
                  <>
                    <Button size="xs" onClick={() => setAttachTo(issue)}>
                      Attach
                    </Button>
                    <Button size="xs" onClick={() => setCreateFrom(issue)}>
                      New product
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => resolve.mutate({ id: issue.id, action: 'resolve' })}
                    >
                      Resolve
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => resolve.mutate({ id: issue.id, action: 'ignore' })}
                    >
                      Ignore
                    </Button>
                  </>
                ) : (
                  <Badge>{humanize(issue.status)}</Badge>
                )}
              </RowActions>
            </BodyRow>
          ))
        )}
      </Panel>

      <Panel
        bleed
        title="Barcode catalog"
        description="Every active code and the product it rings up."
        actions={
          <SearchInput
            size="sm"
            className="w-56"
            value={rawQuery}
            onValueChange={setRawQuery}
            placeholder="Code or product…"
          />
        }
      >
        <HeadRow cols={CODE_COLS}>
          <div>Code</div>
          <div>Product</div>
          <div>Type</div>
          <div className="text-center">Primary</div>
          <div />
        </HeadRow>
        {codes.isLoading ? (
          <TableSkeleton rows={6} cols={4} />
        ) : codes.error ? (
          <ErrorState
            className="m-3"
            message={errorMessage(codes.error)}
            onRetry={() => void codes.refetch()}
          />
        ) : (codes.data?.data ?? []).length === 0 ? (
          <EmptyState compact icon={<Barcode />} title="No codes match" />
        ) : (
          (codes.data?.data ?? []).map((row) =>
            editingCode === row.id && codeDraft ? (
              <CodeEditRow
                key={row.id}
                row={row}
                draft={codeDraft}
                onChange={setCodeDraft}
                saving={saveCode.isPending}
                onCancel={cancelCodeEdit}
                onSave={() =>
                  saveCode.mutate({
                    id: row.id,
                    productId: row.productId,
                    before: {
                      code: row.code,
                      type: row.type,
                      isPrimary: row.isPrimary === true,
                    },
                    after: codeDraft,
                  })
                }
              />
            ) : (
              <BodyRow key={row.id} cols={CODE_COLS}>
                <Code copyable>{row.code}</Code>
                <span className="text-ink truncate text-xs">
                  {row.productName ?? '—'}
                </span>
                <span className="text-2xs text-ink-faint tracking-wider uppercase">
                  {row.type}
                </span>
                <span className="text-center">
                  {row.isPrimary ? (
                    <Check className="text-signal mx-auto size-3.5" />
                  ) : (
                    <span className="text-ink-faint">—</span>
                  )}
                </span>
                <RowActions>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    title="Edit code"
                    onClick={() => startCodeEdit(row)}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                </RowActions>
              </BodyRow>
            )
          )
        )}
        <div className="border-line border-t px-3 py-1.5">
          <Pagination
            page={search.page}
            pageSize={50}
            total={codes.data?.total ?? 0}
            onPageChange={(page) => patch({ page })}
          />
        </div>
      </Panel>

      <AttachBarcodeDialog
        issue={attachTo}
        onClose={() => setAttachTo(null)}
        onDone={refreshIssues}
      />
      <CreateFromBarcodeDialog
        issue={createFrom}
        onClose={() => setCreateFrom(null)}
        onDone={refreshIssues}
      />
    </>
  );
}

/**
 * The catalog row, editable in place.
 *
 * `PUT /barcodes/:id` was reachable from nowhere in the console, so a mistyped
 * digit could only be fixed by deleting the row and adding a new one. The
 * product is not editable here on purpose — moving a code to a different
 * product is the scan-miss queue's "Attach" flow above, which also closes the
 * issue that prompted it.
 */
function CodeEditRow({
  row,
  draft,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  row: BarcodeLookupRow;
  draft: BarcodeDraft;
  onChange: (next: BarcodeDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const valid = draft.code.trim() !== '';
  // An imported row can carry a type outside the API's enum. Show it rather
  // than letting the select fall back to `upc` and rewrite it on save.
  const types = (BARCODE_TYPES as readonly string[]).includes(draft.type)
    ? BARCODE_TYPES
    : [draft.type, ...BARCODE_TYPES];
  return (
    <BodyRow cols={CODE_COLS} editing>
      <Input
        size="sm"
        mono
        autoFocus
        value={draft.code}
        placeholder="Code"
        onChange={(e) => onChange({ ...draft, code: e.currentTarget.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid) onSave();
          if (e.key === 'Escape') onCancel();
        }}
      />
      <span className="text-ink-muted truncate text-xs">{row.productName ?? '—'}</span>
      <Select
        size="sm"
        value={draft.type}
        onChange={(e) => onChange({ ...draft, type: e.currentTarget.value })}
      >
        {types.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Select>
      <div className="flex justify-center">
        <Checkbox
          checked={draft.isPrimary}
          onCheckedChange={(v) => onChange({ ...draft, isPrimary: v })}
        />
      </div>
      <RowActions>
        <Button
          size="icon-sm"
          variant="primary"
          title="Save"
          disabled={!valid}
          loading={saving}
          onClick={onSave}
        >
          <Check className="size-3.5" />
        </Button>
        <Button size="icon-sm" variant="ghost" title="Cancel" onClick={onCancel}>
          <X className="size-3.5" />
        </Button>
      </RowActions>
    </BodyRow>
  );
}

function AttachBarcodeDialog({
  issue,
  onClose,
  onDone,
}: {
  issue: BarcodeReviewIssue | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [type, setType] = useState('upc');
  const [primary, setPrimary] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);

  const attach = useMutation({
    mutationFn: () =>
      api.post<AttachBarcodeResult>(`/barcodes/review/issues/${issue?.id}/add-barcode`, {
        productId: Number(product?.id),
        type,
        isPrimary: primary,
      }),
    onSuccess: (result) => {
      const message = attachSuccessToast(
        result,
        issue?.rawBarcode ?? '',
        product?.name ?? ''
      );
      toast.success(message.title, { description: message.description });
      onDone();
      close();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setConflict(err.message);
        return;
      }
      toast.error('Could not attach the barcode', { description: errorMessage(err) });
    },
  });

  function close() {
    setProduct(null);
    setPrimary(false);
    setType('upc');
    setConflict(null);
    onClose();
  }

  return (
    <Dialog
      open={issue !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title="Attach this code to a product"
      description={
        issue
          ? `Scanned ${num(issue.totalCount)}× — last seen ${relativeTime(issue.lastSeenAt)}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!product}
            loading={attach.isPending}
            onClick={() => attach.mutate()}
          >
            Attach barcode
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-2xs text-ink-faint tracking-wider uppercase">Code</span>
          <Code>{issue?.rawBarcode}</Code>
        </div>

        {conflict ? (
          <div className="border-negative/40 bg-negative-muted text-ink rounded border p-2.5 text-xs">
            <div className="text-negative font-medium">This code is already taken</div>
            <p className="text-ink-muted mt-0.5">{conflict}</p>
            <p className="text-2xs text-ink-faint mt-1">
              Find the product that owns it in the barcode catalog below, or remove the
              code from that product first.
            </p>
          </div>
        ) : null}

        <ProductPicker selected={product} onSelect={setProduct} />

        <div className="flex items-end gap-3">
          <Field label="Code type" className="w-32">
            <Select
              size="sm"
              value={type}
              onChange={(e) => setType(e.currentTarget.value)}
            >
              {['upc', 'plu', 'alt', 'ean', 'internal'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Checkbox
            className="h-7"
            checked={primary}
            onCheckedChange={setPrimary}
            label="Make it the primary code"
          />
        </div>
      </div>
    </Dialog>
  );
}

function CreateFromBarcodeDialog({
  issue,
  onClose,
  onDone,
}: {
  issue: BarcodeReviewIssue | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number | null>(null);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [taxable, setTaxable] = useState(false);
  const [ebtEligible, setEbtEligible] = useState(false);

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/barcodes/review/issues/${issue?.id}/create-product`, {
        name: name.trim(),
        price: price ?? 0,
        departmentId,
        taxable,
        ebtEligible,
        type: 'upc',
      }),
    onSuccess: () => {
      toast.success(`${name.trim()} created and linked to ${issue?.rawBarcode}`);
      onDone();
      close();
    },
    onError: (err) =>
      toast.error('Could not create the product', { description: errorMessage(err) }),
  });

  function close() {
    setName('');
    setPrice(null);
    setDepartmentId(null);
    setTaxable(false);
    setEbtEligible(false);
    onClose();
  }

  return (
    <Dialog
      open={issue !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title="Create a product for this code"
      description="For a genuinely new item. If the item already exists, attach the code instead."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || price === null}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create product
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-2xs text-ink-faint tracking-wider uppercase">Code</span>
          <Code>{issue?.rawBarcode}</Code>
        </div>
        <Field label="Name" required>
          <Input
            autoFocus
            size="sm"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price" required>
            <MoneyInput size="sm" value={price} onValueChange={setPrice} />
          </Field>
          <Field label="Department">
            <Select
              size="sm"
              value={departmentId ?? ''}
              onChange={(e) =>
                setDepartmentId(
                  e.currentTarget.value ? Number(e.currentTarget.value) : null
                )
              }
            >
              <option value="">—</option>
              {(departments.data?.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="flex gap-4">
          <Checkbox checked={taxable} onCheckedChange={setTaxable} label="Taxable" />
          <Checkbox
            checked={ebtEligible}
            onCheckedChange={setEbtEligible}
            label="EBT eligible"
          />
        </div>
      </div>
    </Dialog>
  );
}

/** Search-and-pick one product. Used wherever a screen needs a product id. */
function ProductPicker({
  selected,
  onSelect,
}: {
  selected: ProductSummary | null;
  onSelect: (product: ProductSummary | null) => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);

  const results = useQuery({
    queryKey: ['products', 'picker', debounced],
    queryFn: () =>
      api.get<ProductListResponse>('/products', {
        params: { search: debounced, pageSize: 12, sort: 'name', order: 'asc' },
      }),
    enabled: debounced.trim().length >= 2,
  });

  if (selected) {
    return (
      <div className="border-signal-line bg-signal-muted flex items-center gap-2 rounded border px-2.5 py-2">
        <div className="min-w-0 flex-1">
          <div className="text-ink truncate text-sm">{selected.name}</div>
          <div className="text-2xs text-ink-faint truncate">
            {selected.department ?? 'No department'} · {money(selected.price)}
          </div>
        </div>
        <Button size="xs" variant="ghost" onClick={() => onSelect(null)}>
          Change
        </Button>
      </div>
    );
  }

  return (
    <div>
      <SearchInput
        size="sm"
        value={query}
        onValueChange={setQuery}
        placeholder="Find a product by name or barcode…"
      />
      <div className="border-line mt-1.5 max-h-56 overflow-y-auto rounded border">
        {debounced.trim().length < 2 ? (
          <p className="text-2xs text-ink-faint p-3">Type at least two characters.</p>
        ) : results.isLoading ? (
          <TableSkeleton rows={4} cols={3} />
        ) : (results.data?.data ?? []).length === 0 ? (
          <p className="text-2xs text-ink-faint p-3">No product matches.</p>
        ) : (
          (results.data?.data ?? []).map((p) => (
            <button
              key={String(p.id)}
              type="button"
              onClick={() => onSelect(p)}
              className="border-line-soft hover:bg-surface-hover flex w-full items-center gap-2 border-b px-2.5 py-1.5 text-left last:border-0"
            >
              <span className="text-ink min-w-0 flex-1 truncate text-xs">{p.name}</span>
              <span className="text-2xs text-ink-faint shrink-0">
                {p.department ?? '—'}
              </span>
              <span className="text-2xs text-ink-muted shrink-0 font-mono tabular-nums">
                {money(p.price)}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/* ── Labels: the print queue ──────────────────────────────────────────────── */

interface LabelQueueRow {
  id: number;
  productId: number;
  productName: string | null;
  currentPrice: number | null;
  status: string;
  reason: string;
  oldPrice: number | null;
  newPrice: number | null;
  enqueuedAt: string;
  claimedAt: string | null;
  claimedByStation: string | null;
  printedAt: string | null;
  printedByStation: string | null;
  discardedAt: string | null;
}

type LabelQueueSummary = Record<'pending' | 'claimed' | 'printed' | 'discarded', number>;

const LABEL_COLS = 'minmax(0,1fr) 7rem 10rem 7rem 8rem 9rem';

function LabelsTab({
  search,
  patch,
}: {
  search: OrganizeSearch;
  patch: (next: Partial<OrganizeSearch>) => void;
}) {
  const queryClient = useQueryClient();
  const [enqueueOpen, setEnqueueOpen] = useState(false);
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [pendingDiscard, setPendingDiscard] = useState<LabelQueueRow | null>(null);

  const summary = useQuery({
    queryKey: ['labels', 'summary'],
    queryFn: () => api.get<LabelQueueSummary>('/labels/queue/summary'),
  });

  const queue = useQuery({
    queryKey: ['labels', 'queue', search.labelStatus, search.page],
    queryFn: () =>
      api.get<PaginatedResponse<LabelQueueRow>>('/labels/queue', {
        params: {
          status: search.labelStatus === 'all' ? undefined : search.labelStatus,
          page: search.page,
          pageSize: 50,
        },
      }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['labels'] });
  }

  const enqueue = useMutation({
    mutationFn: (productId: number) =>
      api.post<{ enqueued: number }>('/labels/queue', {
        productIds: [productId],
        reason: 'manual',
      }),
    onSuccess: () => {
      toast.success(`${picked?.name} queued for a shelf label`);
      setEnqueueOpen(false);
      setPicked(null);
      refresh();
    },
    onError: (err) =>
      toast.error('Could not queue the label', { description: errorMessage(err) }),
  });

  const discard = useMutation({
    mutationFn: (row: LabelQueueRow) =>
      api.post(`/labels/queue/${row.id}/discard`, {
        reason: 'Discarded from the console',
      }),
    onSuccess: () => {
      toast.success('Row discarded');
      setPendingDiscard(null);
      refresh();
    },
    onError: (err) =>
      toast.error('Could not discard the row', { description: errorMessage(err) }),
  });

  const requeue = useMutation({
    mutationFn: (row: LabelQueueRow) => api.post(`/labels/queue/${row.id}/requeue`, {}),
    onSuccess: () => {
      toast.success('Claim released — back to pending');
      refresh();
    },
    onError: (err) =>
      toast.error('Could not requeue the row', { description: errorMessage(err) }),
  });

  const rows = queue.data?.data ?? [];

  return (
    <>
      <StatRow>
        <Stat
          label="Pending"
          value={num(summary.data?.pending ?? 0)}
          tone="signal"
          sub="waiting to print"
        />
        <Stat
          label="Claimed"
          value={num(summary.data?.claimed ?? 0)}
          sub="held by a station"
        />
        <Stat label="Printed" value={num(summary.data?.printed ?? 0)} sub="done" />
        <Stat
          label="Discarded"
          value={num(summary.data?.discarded ?? 0)}
          sub="never printed"
        />
      </StatRow>

      <Panel title="How this queue works">
        <p className="text-ink-muted text-xs leading-relaxed">
          Nothing prints from this console. A separate Windows label station polls this
          queue, <strong className="text-ink">claims</strong> a batch of pending rows,
          prints them, then confirms each row{' '}
          <strong className="text-ink">printed</strong>. Rows appear here automatically
          whenever a product price changes, and manually when you queue one. A claim that
          goes stale for five minutes is released back to pending on its own, so a station
          that crashes mid-run does not strand its labels — the Requeue button below does
          the same thing immediately.
        </p>
      </Panel>

      <Panel
        bleed
        title="Print queue"
        actions={
          <div className="flex items-center gap-1.5">
            <Button asChild size="xs">
              <Link to="/settings/label-templates" search={{ kind: 'shelf' }}>
                Design templates
              </Link>
            </Button>
            <ChipGroup<LabelStatus>
              value={search.labelStatus}
              onChange={(v) => patch({ labelStatus: v, page: 1 })}
              options={[
                { value: 'pending', label: 'Pending', count: summary.data?.pending },
                { value: 'claimed', label: 'Claimed', count: summary.data?.claimed },
                { value: 'printed', label: 'Printed' },
                { value: 'discarded', label: 'Discarded' },
                { value: 'all', label: 'All' },
              ]}
            />
            <Button
              size="xs"
              icon={<Printer className="size-3" />}
              onClick={() => setEnqueueOpen(true)}
            >
              Queue a product
            </Button>
          </div>
        }
      >
        <HeadRow cols={LABEL_COLS}>
          <div>Product</div>
          <div>Reason</div>
          <div className="text-right">Price change</div>
          <div>Enqueued</div>
          <div>Station</div>
          <div />
        </HeadRow>

        {queue.isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : queue.error ? (
          <ErrorState
            className="m-3"
            message={errorMessage(queue.error)}
            onRetry={() => void queue.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            icon={<Printer />}
            title="Queue is empty"
            description="Shelf labels land here when a price changes, or when you queue a product."
          />
        ) : (
          rows.map((row) => (
            <BodyRow key={row.id} cols={LABEL_COLS}>
              <span className="text-ink truncate text-xs">
                {row.productName ?? `Product ${row.productId}`}
              </span>
              <span>
                <Badge tone={row.reason === 'price_change' ? 'signal' : 'neutral'}>
                  {humanize(row.reason)}
                </Badge>
              </span>
              <span className="text-2xs text-ink-muted text-right font-mono tabular-nums">
                {row.oldPrice == null && row.newPrice == null
                  ? '—'
                  : `${row.oldPrice == null ? '—' : money(row.oldPrice)} → ${
                      row.newPrice == null ? '—' : money(row.newPrice)
                    }`}
              </span>
              <span className="text-2xs text-ink-faint" title={smartDate(row.enqueuedAt)}>
                {relativeTime(row.enqueuedAt)}
              </span>
              <span className="text-2xs text-ink-faint truncate">
                {row.printedByStation ?? row.claimedByStation ?? (
                  <StatusBadge status={row.status} />
                )}
              </span>
              <RowActions>
                {row.status === 'claimed' ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<RotateCcw className="size-3" />}
                    onClick={() => requeue.mutate(row)}
                  >
                    Requeue
                  </Button>
                ) : null}
                {row.status === 'pending' || row.status === 'claimed' ? (
                  <Button
                    size="xs"
                    variant="danger"
                    onClick={() => setPendingDiscard(row)}
                  >
                    Discard
                  </Button>
                ) : (
                  <StatusBadge status={row.status} />
                )}
              </RowActions>
            </BodyRow>
          ))
        )}
        <div className="border-line border-t px-3 py-1.5">
          <Pagination
            page={search.page}
            pageSize={50}
            total={queue.data?.total ?? 0}
            onPageChange={(page) => patch({ page })}
          />
        </div>
      </Panel>

      <Dialog
        open={enqueueOpen}
        onOpenChange={(open) => {
          setEnqueueOpen(open);
          if (!open) setPicked(null);
        }}
        title="Queue a shelf label"
        description="The label station prints it on its next poll."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEnqueueOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!picked}
              loading={enqueue.isPending}
              onClick={() => {
                if (picked) enqueue.mutate(Number(picked.id));
              }}
            >
              Queue label
            </Button>
          </>
        }
      >
        <ProductPicker selected={picked} onSelect={setPicked} />
      </Dialog>

      <ConfirmDialog
        open={pendingDiscard !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDiscard(null);
        }}
        title="Discard this label row?"
        description={pendingDiscard?.productName ?? undefined}
        confirmLabel="Discard"
        loading={discard.isPending}
        consequences={[
          'The label is never printed — the shelf keeps its current tag.',
          'Any later price change queues the product again.',
        ]}
        onConfirm={() => {
          if (pendingDiscard) discard.mutate(pendingDiscard);
        }}
      />
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'printed'
      ? 'positive'
      : status === 'discarded'
        ? 'neutral'
        : status === 'claimed'
          ? 'warn'
          : 'signal';
  return <Badge tone={tone}>{humanize(status)}</Badge>;
}
